<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft, Plus } from '@lucide/vue'
import { Badge, Button, PageHeader, Section, Spinner, Table } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import {
  deleteProviderCapabilityKey,
  fetchModelCapabilityCatalog,
  fetchProviderCapabilityKeys,
  saveProviderCapabilityKey,
} from '../../api/model-capabilities.js'
import { MODEL_CAPABILITIES } from './model-capability.js'
import KeyPool from './provider/KeyPool.vue'
import ModelProviderModal from './provider/ModelProviderModal.vue'
import ProviderCapabilityModal from './provider/ProviderCapabilityModal.vue'

const props = defineProps({
  name: { type: String, required: true },
})
const { t } = useI18n()
const router = useRouter()
const catalog = ref(null)
const provider = ref(null)
const keyPools = ref([])
const loading = ref(true)
const error = ref('')
const providerModalOpen = ref(false)
const capabilityModalOpen = ref(false)
const editingCapability = ref(null)

const capabilities = computed(() => MODEL_CAPABILITIES
  .flatMap(capability => catalog.value[capability].provider_capabilities)
  .filter(capability => capability.provider_id === provider.value.id)
  .map(capability => ({
    ...capability,
    model_count: catalog.value[capability.capability].models
      .filter(model => model.provider_capability_id === capability.id)
      .length,
  })))
const columns = computed(() => [
  { key: 'capability', label: t('visualGameAdmin.llm.providers.capabilities.capability'), width: '120px' },
  { key: 'code', label: t('visualGameAdmin.llm.providers.capabilities.code'), width: '150px', mono: true },
  { key: 'adapter', label: t('visualGameAdmin.llm.providers.table.adapter'), width: '140px', mono: true },
  { key: 'credentials', label: t('visualGameAdmin.llm.providers.basic.credentialSource'), width: '150px' },
  { key: 'models', label: t('visualGameAdmin.llm.providers.table.models'), width: '90px', align: 'right', mono: true },
  { key: 'enabled', label: t('visualGameAdmin.llm.providers.table.enabled'), width: '100px' },
  { key: 'actions', label: t('visualGameAdmin.llm.providers.table.actions'), width: '190px', align: 'right' },
])

onMounted(load)
watch(() => props.name, load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    applyCatalog(await fetchModelCapabilityCatalog())
    await loadKeyPools()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyCatalog(nextCatalog) {
  const nextProvider = nextCatalog.providers.find(row => row.code === props.name)
  if (!nextProvider) throw new Error(`Provider not found: ${props.name}`)
  catalog.value = nextCatalog
  provider.value = nextProvider
}

async function loadKeyPools() {
  const keyPoolCapabilities = capabilities.value.filter(capability => (
    capability.credential_source === 'key_pool'
  ))
  keyPools.value = await Promise.all(keyPoolCapabilities.map(async providerCapability => ({
    provider_capability: providerCapability,
    ...await fetchProviderCapabilityKeys(providerCapability.id),
  })))
}

async function catalogSaved(nextCatalog) {
  applyCatalog(nextCatalog)
  await loadKeyPools()
}

function openNewCapability() {
  editingCapability.value = null
  capabilityModalOpen.value = true
}

function openCapability(capability) {
  editingCapability.value = capability
  capabilityModalOpen.value = true
}

function openModels(capability) {
  router.push({
    name: 'model-capabilities',
    query: { capability: capability.capability, provider_capability_id: capability.id },
  })
}

function backToList() {
  router.push({ name: 'llm-providers' })
}
</script>

<template>
  <div class="provider-detail vga-page">
    <PageHeader :title="provider ? provider.display_name : name">
      <template #actions>
        <Badge v-if="provider" :variant="provider.enabled ? 'success' : 'neutral'">
          {{ t(`visualGameAdmin.llm.providers.status.${provider.enabled ? 'enabled' : 'disabled'}`) }}
        </Badge>
        <Button v-if="provider" variant="secondary" size="sm" @click="providerModalOpen = true">
          {{ t('visualGameAdmin.llm.providers.editProvider') }}
        </Button>
        <Button variant="ghost" size="sm" @click="backToList">
          <ArrowLeft :size="16" />
          {{ t('visualGameAdmin.llm.providers.detail.back') }}
        </Button>
      </template>
    </PageHeader>

    <Section v-if="loading">
      <div class="provider-detail__state"><Spinner :label="t('visualGameAdmin.common.loading')" /></div>
    </Section>
    <Section v-else-if="error">
      <div class="provider-detail__state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
    </Section>
    <template v-else>
      <Section :title="t('visualGameAdmin.llm.providers.capabilities.title')" :padded="false">
        <template #actions>
          <Button variant="primary" size="sm" @click="openNewCapability">
            <Plus :size="16" />
            {{ t('visualGameAdmin.llm.providers.capabilities.add') }}
          </Button>
        </template>
        <Table :columns="columns" :rows="capabilities">
          <template #capability="{ row }">{{ t(`visualGameAdmin.llm.capabilities.types.${row.capability}`) }}</template>
          <template #credentials="{ row }">{{ t(`visualGameAdmin.llm.providers.credentials.${row.credential_source}`) }}</template>
          <template #models="{ row }">{{ row.model_count }}</template>
          <template #enabled="{ row }">
            <Badge :variant="row.enabled ? 'success' : 'neutral'">
              {{ t(`visualGameAdmin.llm.providers.status.${row.enabled ? 'enabled' : 'disabled'}`) }}
            </Badge>
          </template>
          <template #actions="{ row }">
            <div class="provider-detail__actions">
              <Button variant="ghost" size="sm" @click="openCapability(row)">
                {{ t('visualGameAdmin.llm.providers.capabilities.edit') }}
              </Button>
              <Button variant="secondary" size="sm" @click="openModels(row)">
                {{ t('visualGameAdmin.llm.providers.capabilities.models') }}
              </Button>
            </div>
          </template>
        </Table>
      </Section>

      <KeyPool
        v-for="keyPool in keyPools"
        :key="keyPool.provider_capability_id"
        :vendor="`${keyPool.provider_capability.capability}:${keyPool.provider_capability.code}`"
        :provider-base-url="keyPool.provider_capability.base_url"
        :keys="keyPool.keys"
        :save-key="(body) => saveProviderCapabilityKey(keyPool.provider_capability.id, body)"
        :delete-key="(keyId) => deleteProviderCapabilityKey(keyPool.provider_capability.id, keyId)"
        @changed="load"
      />
    </template>

    <ModelProviderModal
      v-if="provider"
      v-model:open="providerModalOpen"
      :provider="provider"
      @saved="catalogSaved"
    />
    <ProviderCapabilityModal
      v-if="provider && catalog"
      v-model:open="capabilityModalOpen"
      :catalog="catalog"
      :provider="provider"
      :provider-capability="editingCapability"
      @saved="catalogSaved"
    />
  </div>
</template>

<style scoped lang="scss">
.provider-detail {
  &__state {
    display: grid;
    justify-items: start;
    gap: var(--drama-space-md);
    color: var(--drama-color-text-muted);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--drama-space-xs);
  }
}
</style>
